import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { OpenInIcon, CardWatermarkIcon } from '../components/icons.js';
import './insights-viewer.css';

export interface LibraryItem {
  id: string;
  name: string;
  kind: 'Dashboard' | 'Report';
  folder: string;
}
export interface LibrarySelection {
  kind: 'All' | 'Dashboard' | 'Report';
  folder?: string;
  itemIds?: string[];
}
export function descendantFolderIds(
  id: string,
  folders: ReadonlyArray<{ id: string; parentId: string | null }>,
): Set<string> {
  const ids = new Set([id]);
  let size = 0;
  while (size !== ids.size) {
    size = ids.size;
    for (const folder of folders)
      if (folder.parentId && ids.has(folder.parentId)) ids.add(folder.id);
  }
  return ids;
}
interface Props {
  selection?: LibrarySelection;

  navigationToggle?: ReactNode;
  items: LibraryItem[];
  loading?: boolean;
  error?: boolean;
  onRetry?(): void;
  onOpen(item: LibraryItem): void;
  /** CSAGE-556: the content types this session may see; the others get no chip. */
  kinds?: ReadonlyArray<LibraryItem['kind']>;
}
export function InsightsLibrary({
  items,
  loading,
  error,
  onRetry,
  onOpen,
  navigationToggle,
  selection,
  kinds = ['Dashboard', 'Report'],
}: Props) {
  const [layout, setLayout] = useState<'cards' | 'table'>(() => {
    try {
      return localStorage.getItem('sage.insights.library.layout') === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });
  function changeLayout(next: 'cards' | 'table'): void {
    setLayout(next);
    try {
      localStorage.setItem('sage.insights.library.layout', next);
    } catch {
      /* Storage can be disabled in embeds. */
    }
  }
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('All');
  const [folder, setFolder] = useState('All folders');
  const [page, setPage] = useState(1);
  const [scopeIds, setScopeIds] = useState<string[] | undefined>();
  useEffect(() => {
    if (!selection) return;
    setKind(selection.kind);
    setFolder(selection.folder ?? 'All folders');
    setScopeIds(selection.itemIds);
    setQuery('');
    setPage(1);
  }, [selection]);
  const folders = useMemo(() => [...new Set(items.map((i) => i.folder))].sort(), [items]);
  const results = items.filter(
    (i) =>
      (kind === 'All' || i.kind === kind) &&
      (scopeIds ? scopeIds.includes(i.id) : folder === 'All folders' || i.folder === folder) &&
      `${i.name} ${i.folder}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const pageSize = 24;
  const pages = Math.max(1, Math.ceil(results.length / pageSize));
  const currentPage = Math.min(page, pages);
  const pageItems = results.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  return (
    <div className="insights-library">
      <header className="insights-library-toolbar">
        {navigationToggle}
        <h1>Insights</h1>
        <div className="insights-library-types" aria-label="Content type">
          {['All', ...kinds].map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={kind === t}
              onClick={() => {
                setKind(t);
                setPage(1);
              }}
            >
              {t === 'All' ? 'All insights' : `${t}s`}{' '}
              <span>
                {loading || error ? '–' : items.filter((i) => t === 'All' || i.kind === t).length}
              </span>
            </button>
          ))}
        </div>
        <div className="insights-library-search">
          <label>
            <span className="sr-only">Search insights</span>
            <input
              type="search"
              placeholder="Search by name or folder…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label>
            <span className="sr-only">Folder</span>
            <select
              value={folder}
              onChange={(e) => {
                setScopeIds(undefined);
                setFolder(e.target.value);
                setPage(1);
              }}
            >
              <option>All folders</option>
              {folder !== 'All folders' && !folders.includes(folder) && <option>{folder}</option>}
              {folders.map((f) => (
                <option key={f}>{f}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="insights-layout-switch" role="group" aria-label="Library view">
          <button
            type="button"
            aria-pressed={layout === 'cards'}
            onClick={() => changeLayout('cards')}
          >
            ▦ Cards
          </button>
          <button
            type="button"
            aria-pressed={layout === 'table'}
            onClick={() => changeLayout('table')}
          >
            ☷ Table
          </button>
        </div>
      </header>
      {loading ? (
        <div className="insights-library-empty" role="status">
          Loading your insights…
        </div>
      ) : error ? (
        <div className="insights-library-empty" role="alert">
          <h2>We couldn’t load your library</h2>
          <p>Please try again to see the insights available to you.</p>
          <button className="btn btn--secondary" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : (
        <>
          <p className="insights-result-count sr-only" role="status">
            {results.length} {results.length === 1 ? 'insight' : 'insights'}
            {query ? ` matching “${query}”` : ' available'}
          </p>
          {results.length ? (
            layout === 'table' ? (
              <div className="insights-table-wrap">
                <table className="data-table data-table--zebra insights-library-table">
                  <caption className="sr-only">Reports and dashboards</caption>
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Type</th>
                      <th scope="col">Folder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((i) => (
                      <tr key={`${i.kind}:${i.id}`}>
                        <td>
                          <button
                            type="button"
                            className="catalog-link"
                            title={i.name}
                            onClick={() => onOpen(i)}
                          >
                            {i.name}
                          </button>
                        </td>
                        <td>{i.kind}</td>
                        <td>{i.folder}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="insights-library-grid">
                {pageItems.map((i) => (
                  <button
                    type="button"
                    className="insights-library-card"
                    data-kind={i.kind}
                    title={`Open ${i.kind.toLowerCase()}: ${i.name}`}
                    key={`${i.kind}:${i.id}`}
                    onClick={() => onOpen(i)}
                  >
                    {/* CSAGE-564: faint subject watermark for visual variety +
                        a sense of what the tile is. */}
                    <CardWatermarkIcon
                      name={i.name}
                      kind={i.kind}
                      className="insights-card-watermark"
                      size={104}
                    />
                    {/* Title leads the card (was a type logo); the single open
                        affordance is the icon top-right — the whole tile is the
                        one click target. */}
                    <span className="insights-card-head">
                      <strong className="insights-card-title">{i.name}</strong>
                      <OpenInIcon className="insights-card-open-icon" size={16} />
                    </span>
                    <span className="insights-card-meta">
                      {i.kind} · {i.folder}
                    </span>
                  </button>
                ))}
              </div>
            )
          ) : (
            <div className="insights-library-empty">
              <h2>{items.length ? 'No matching insights' : 'Your library is empty'}</h2>
              <p>
                {items.length
                  ? 'Try another search or clear your filters.'
                  : 'Reports and dashboards shared with you will appear here.'}
              </p>
              {items.length > 0 && (
                <button
                  className="btn btn--secondary"
                  onClick={() => {
                    setQuery('');
                    setKind('All');
                    setFolder('All folders');
                    setScopeIds(undefined);
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
          {pages > 1 && (
            <nav className="insights-pagination" aria-label="Library pages">
              <span>
                Showing {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, results.length)} of {results.length}
              </span>
              <button
                type="button"
                className="btn btn--secondary"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {pages}
              </span>
              <button
                type="button"
                className="btn btn--secondary"
                disabled={currentPage === pages}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

export function InsightsViewerHeader({
  name,
  kind,
  onRefresh,
  onPdf,
  onManageAccess,
  navigationToggle,
}: {
  navigationToggle?: ReactNode;
  name: string;
  kind: string;
  onBack(): void;
  onRefresh?: (() => void) | undefined;
  /** Download the open dashboard, with its current filters, as a PDF. */
  onPdf?: (() => void) | undefined;
  /** CSAGE-557: open "Manage access" for the open dashboard (share permission only). */
  onManageAccess?: (() => void) | undefined;
}) {
  return (
    <header className="insights-viewer-header">
      {navigationToggle}
      <div>
        <div className="insights-viewer-title">
          {kind && <span className="insights-eyebrow">{kind}</span>}
          <h1 title={name}>{name}</h1>
        </div>
      </div>
      {(onPdf || onRefresh || onManageAccess) && (
        <div className="insights-viewer-actions">
          {onManageAccess && (
            <button type="button" className="btn btn--secondary" onClick={onManageAccess}>
              Manage access
            </button>
          )}
          {onPdf && (
            <button type="button" className="btn btn--secondary" onClick={onPdf}>
              PDF
            </button>
          )}
          {onRefresh && (
            <button type="button" className="btn btn--secondary" onClick={onRefresh}>
              Refresh
            </button>
          )}
        </div>
      )}
    </header>
  );
}
